import {
  type AnimationClip,
  AnimationMixer,
  LoopOnce,
  type Object3D,
} from 'three';

export type Act = 'Greet' | 'Acknowledge' | 'Complete' | 'Stretch';
export interface ActingSnapshot {
  selected?: Act;
  actions: { name: Act; time: number; weight: number }[];
}

/** Clips animate only the Acting control. Procedural posing consumes that
 * control afterwards, preserving hand/prop contact even during interruptions. */
export function createActing(scene: Object3D, clips: AnimationClip[]) {
  const mixer = new AnimationMixer(scene);
  const actions = clips.map((clip) => {
    const action = mixer.clipAction(clip).setLoop(LoopOnce, 1);
    action.clampWhenFinished = true;
    action.play();
    action.paused = true;
    action.setEffectiveWeight(0);
    return { name: clip.name as Act, action };
  });
  let selected: Act | undefined;
  return {
    moving() {
      return actions.some(
        ({ action }) =>
          action.weight > 0.01 && action.time < action.getClip().duration,
      );
    },
    start(name: Act) {
      const entry = actions.find((entry) => entry.name === name);
      if (
        !entry ||
        (selected === name &&
          entry.action.time < entry.action.getClip().duration)
      )
        return;
      selected = name;
      entry.action.time = 0;
      entry.action.enabled = true;
    },
    advance(dt: number, animate: boolean, resting: boolean, paused: boolean) {
      if (paused || (!resting && selected !== 'Acknowledge'))
        selected = undefined;
      for (const { name, action } of actions) {
        const target = animate && name === selected ? 1 : 0;
        const weight = animate
          ? action.weight + (target - action.weight) * (1 - Math.exp(-10 * dt))
          : 0;
        action.setEffectiveWeight(weight);
        if (animate)
          action.time = Math.min(action.time + dt, action.getClip().duration);
      }
      // Time is advanced explicitly so restoring a pose on a second GL surface
      // is exact, including clips that were halfway through fading out.
      mixer.update(0);
    },
    snapshot(): ActingSnapshot {
      return {
        selected,
        actions: actions.map(({ name, action }) => ({
          name,
          time: action.time,
          weight: action.weight,
        })),
      };
    },
    restore(snapshot: ActingSnapshot) {
      selected = snapshot.selected;
      for (const saved of snapshot.actions) {
        const entry = actions.find((entry) => entry.name === saved.name);
        if (entry) {
          entry.action.time = saved.time;
          entry.action.setEffectiveWeight(saved.weight);
        }
      }
      mixer.update(0);
    },
    dispose() {
      mixer.stopAllAction();
      mixer.uncacheRoot(scene);
    },
  };
}
